import get from 'lodash/get';
import { ref } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';

import { env } from '@/env';

const POLL_INTERVAL_MS = 15 * 60 * 1000;
const VERSION_URL = '/version.json';
export const APP_UPDATED_KEY = 'app-updated';
export const APP_UPDATE_NOTIFIED_REVISION_KEY = 'app-update-notified-revision';
// Two-stage staleness: nudge the user with a toast at 3 days, then force a
// soft-reload on next navigation at 7 days. Most users pick up new code
// naturally (closed laptop, manual reload), so we only intervene on the
// long-tail of stale tabs — gently first, then firmly.
export const NUDGE_THRESHOLD_MS = 3 * 24 * 60 * 60 * 1000;
export const FORCE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

export const updateRecommended = ref(false);
export const updateForced = ref(false);
export const latestRevision = ref<string | null>(null);

let pollHandle: ReturnType<typeof setInterval> | null = null;

export async function checkForAppUpdate(): Promise<void> {
  if (typeof document !== 'undefined' && document.hidden) return;

  const current = env.revision;
  if (!current || current === 'unknown') return;

  try {
    const response = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!response.ok) return;
    const data: unknown = await response.json();
    const revision = get(data, 'revision');
    if (typeof revision !== 'string') return;
    latestRevision.value = revision;
    if (revision === current) return;

    const rawServerBuildTime = get(data, 'buildTime');
    const serverBuildTime =
      typeof rawServerBuildTime === 'string' ? Date.parse(rawServerBuildTime) : NaN;
    const clientBuildTime = env.buildTime ? Date.parse(env.buildTime) : NaN;
    if (Number.isNaN(serverBuildTime) || Number.isNaN(clientBuildTime)) {
      // Missing/unparseable timestamp on either side: stay silent rather than
      // soft-reload on every deploy. A bad clock or pre-rollout build hides the
      // staleness gate, but the chunk-load reload guard still catches truly broken tabs.
      return;
    }
    const ageMs = serverBuildTime - clientBuildTime;
    if (ageMs > NUDGE_THRESHOLD_MS) updateRecommended.value = true;
    if (ageMs > FORCE_THRESHOLD_MS) updateForced.value = true;
  } catch {
    // Polling errors (offline, redeploy mid-flight, JSON parse) are expected.
    // The next interval or visibility change will retry.
  }
}

const onVisibilityChange = (): void => {
  if (!document.hidden) void checkForAppUpdate();
};

export function useAppVersion(): {
  updateRecommended: typeof updateRecommended;
  updateForced: typeof updateForced;
  latestRevision: typeof latestRevision;
  currentRevision: string | undefined;
} {
  if (!pollHandle && typeof window !== 'undefined' && env.revision && env.revision !== 'unknown') {
    pollHandle = setInterval(checkForAppUpdate, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    void checkForAppUpdate();
  }
  return {
    updateRecommended,
    updateForced,
    latestRevision,
    currentRevision: env.revision,
  };
}

/**
 * Vue Router beforeEach guard. When a stale build has been detected, intercepts
 * the next SPA navigation and replaces it with a full page load so the user
 * picks up fresh hashed assets. Returns false to abort the SPA nav (the
 * production push/replace wrappers in src/router.js suppress the warn that
 * follows when updateForced is true).
 */
export function softReloadGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): false | undefined {
  // Skip on the initial navigation (from.name is undefined) so we never reload mid-bootstrap.
  if (!updateForced.value || !from.name) return undefined;
  sessionStorage.setItem(APP_UPDATED_KEY, '1');
  window.location.assign(to.fullPath);
  return false;
}

/** Internal — exposed only for tests to reset module-scoped state between cases. */
export function _resetAppVersionForTests(): void {
  if (pollHandle) {
    clearInterval(pollHandle);
    pollHandle = null;
  }
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }
  updateRecommended.value = false;
  updateForced.value = false;
  latestRevision.value = null;
}
